import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Plus,
  Loader2,
  Trash2,
  Star,
  StarOff,
  FileText,
  Building2,
  UtensilsCrossed,
  HardHat,
  Home,
  ChevronRight,
  Sparkles,
  Layers3,
  ReceiptText,
  LayoutTemplate,
  Move,
  Pin,
  PanelsTopLeft,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useProposalTemplates,
  useDeleteTemplate,
  useSetDefaultTemplate,
} from "../../hooks/useProposalTemplates";
import type {
  BuilderMode,
  FixedPagePolicy,
  ProposalTemplate,
  ClientType,
} from "../../types";

const CLIENT_TYPE_CONFIG: Record<
  ClientType,
  { label: string; icon: React.ReactNode; color: string; description: string }
> = {
  construction: {
    label: "Construction",
    icon: <HardHat className="h-4 w-4" />,
    color: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    description: "Final cleans, post-renovation, progress clean scopes",
  },
  restaurant: {
    label: "Restaurant",
    icon: <UtensilsCrossed className="h-4 w-4" />,
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    description: "FOH, BOH, sanitation, recurring restaurant cleaning",
  },
  commercial: {
    label: "Commercial",
    icon: <Building2 className="h-4 w-4" />,
    color: "text-purple-400 bg-purple-500/10 border-purple-500/20",
    description: "Offices, retail, common areas, janitorial service",
  },
  residential: {
    label: "Residential",
    icon: <Home className="h-4 w-4" />,
    color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    description: "Homes, move-in/out, recurring housekeeping",
  },
  other: {
    label: "Other",
    icon: <FileText className="h-4 w-4" />,
    color: "text-white/40 bg-white/5 border-white/10",
    description: "Custom client type and service model",
  },
};

const BUILDER_MODE_CONFIG: Record<
  BuilderMode,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  prebuilt: {
    label: "Pre-built",
    icon: <LayoutTemplate className="h-4 w-4" />,
    color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    description: "Simpler template-first experience",
  },
  drag_drop: {
    label: "Drag & drop",
    icon: <Move className="h-4 w-4" />,
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    description: "Builder-ready template mode",
  },
};

const FIXED_PAGE_POLICY_CONFIG: Record<
  FixedPagePolicy,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  template_defined: {
    label: "Template pages",
    icon: <PanelsTopLeft className="h-3.5 w-3.5" />,
    color: "text-white/45 bg-white/5 border-white/10",
    description: "Template controls its own page mix",
  },
  brand_defined: {
    label: "Brand-defined fixed pages",
    icon: <Pin className="h-3.5 w-3.5" />,
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    description: "Leans on Brand Setup fixed pages",
  },
  mixed: {
    label: "Mixed fixed pages",
    icon: <Pin className="h-3.5 w-3.5" />,
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    description: "Combines template and brand page control",
  },
};

function getTemplateClientType(template: ProposalTemplate): ClientType {
  const raw =
    template.client_type ??
    (template.category as ClientType | null | undefined) ??
    "other";

  if (
    raw === "construction" ||
    raw === "restaurant" ||
    raw === "commercial" ||
    raw === "residential" ||
    raw === "other"
  ) {
    return raw;
  }

  return "other";
}

function getTemplateServiceCount(template: ProposalTemplate): number {
  if (
    Array.isArray(template.template_services) &&
    template.template_services.length > 0
  ) {
    return template.template_services.length;
  }

  if (Array.isArray(template.default_line_items)) {
    return template.default_line_items.length;
  }

  return 0;
}

function getTemplatePageCount(template: ProposalTemplate): number {
  if (
    Array.isArray(template.template_static_pages) &&
    template.template_static_pages.length > 0
  ) {
    return template.template_static_pages.length;
  }

  if (Array.isArray(template.default_static_page_ids)) {
    return Array.from(
      new Set(
        template.default_static_page_ids.filter(
          (pageId): pageId is string =>
            typeof pageId === "string" && pageId.trim().length > 0
        )
      )
    ).length;
  }

  return 0;
}

function getTemplateTaxRatePercent(template: ProposalTemplate): string {
  const rawRate =
    typeof template.tax_rate === "number"
      ? template.tax_rate
      : typeof template.default_tax_rate === "number"
        ? template.default_tax_rate
        : 0;

  return (rawRate * 100).toFixed(1);
}

function getTemplateBuilderMode(template: ProposalTemplate): BuilderMode {
  return template.builder_mode === "drag_drop" ? "drag_drop" : "prebuilt";
}

function getTemplateFixedPagePolicy(template: ProposalTemplate): FixedPagePolicy {
  if (
    template.fixed_page_policy === "brand_defined" ||
    template.fixed_page_policy === "mixed"
  ) {
    return template.fixed_page_policy;
  }

  return "template_defined";
}

function isTemplateReady(template: ProposalTemplate): boolean {
  return (
    getTemplateServiceCount(template) > 0 &&
    getTemplatePageCount(template) > 0 &&
    (typeof template.tax_rate === "number" ||
      typeof template.default_tax_rate === "number")
  );
}

export function TemplateManager() {
  const navigate = useNavigate();
  const { data: templates = [], isLoading } = useProposalTemplates();
  const deleteTemplate = useDeleteTemplate();
  const setDefault = useSetDefaultTemplate();

  const summary = useMemo(() => {
    const uniqueClientTypes = new Set(
      templates.map((template) => getTemplateClientType(template))
    ).size;

    const prebuiltCount = templates.filter(
      (template) => getTemplateBuilderMode(template) === "prebuilt"
    ).length;

    const dragDropCount = templates.filter(
      (template) => getTemplateBuilderMode(template) === "drag_drop"
    ).length;

    const readyCount = templates.filter((template) => isTemplateReady(template)).length;

    const defaultTemplate = templates.find((t) => t.is_default);

    return {
      uniqueClientTypes,
      prebuiltCount,
      dragDropCount,
      readyCount,
      defaultTemplate,
    };
  }, [templates]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f0f0f]">
        <Loader2 className="h-5 w-5 animate-spin text-white/30" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-amber-400/80">
              Proposal templates
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">
              Template library
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-white/45">
              Templates sit between Brand Setup and Proposals. They define the
              reusable service mix, included pages, default tax settings, AI
              context, builder mode, and fixed-page direction for proposal creation.
            </p>
          </div>

          <Link
            to="/proposal-builder/templates/new"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
          >
            <Plus className="h-4 w-4" />
            New template
          </Link>
        </div>

        {templates.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                label="Templates"
                value={String(templates.length)}
                icon={<Layers3 className="h-4 w-4" />}
              />
              <MetricCard
                label="Default template"
                value={summary.defaultTemplate?.name ?? "Not set"}
                icon={<Star className="h-4 w-4" />}
              />
              <MetricCard
                label="Pre-built templates"
                value={String(summary.prebuiltCount)}
                icon={<LayoutTemplate className="h-4 w-4" />}
              />
              <MetricCard
                label="Drag & drop templates"
                value={String(summary.dragDropCount)}
                icon={<Move className="h-4 w-4" />}
              />
            </div>

            <div className="mb-6 grid gap-4 md:grid-cols-3">
              <MetricCard
                label="Client types"
                value={String(summary.uniqueClientTypes)}
                icon={<ReceiptText className="h-4 w-4" />}
              />
              <MetricCard
                label="Ready for proposals"
                value={String(summary.readyCount)}
                icon={<CheckCircle2 className="h-4 w-4" />}
              />
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-center gap-2 text-white/35">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-xs uppercase tracking-[0.14em]">
                    Builder foundation
                  </span>
                </div>
                <p className="mt-3 text-sm leading-6 text-white/55">
                  Templates now carry builder metadata like builder mode and
                  fixed-page policy even before the full drag-and-drop editor is finished.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSetDefault={() => void setDefault.mutateAsync(template.id)}
                  onDelete={(id) => deleteTemplate.mutate(id)}
                  isDeleting={
                    deleteTemplate.isPending &&
                    deleteTemplate.variables === template.id
                  }
                  isSettingDefault={
                    setDefault.isPending && setDefault.variables === template.id
                  }
                  onClick={() =>
                    navigate({
                      to: "/proposal-builder/templates/$id",
                      params: { id: template.id },
                    })
                  }
                />
              ))}
            </div>
          </>
        )}

        <div className="mt-10 rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-amber-500/20 bg-amber-500/10">
              <Sparkles className="h-4 w-4 text-amber-400" />
            </div>

            <div>
              <p className="mb-1 text-sm font-medium text-white/80">
                How templates work now
              </p>

              <p className="text-sm leading-relaxed text-white/40">
                Brand Setup defines your company-wide foundation: logo, colors,
                cover letter base, reusable pages, service catalog, and richer
                brand assets and fonts. Templates build on top of that foundation
                and define the default blueprint for a type of job, including
                builder mode and fixed-page direction. Proposals are then created
                from a template for a specific customer.
              </p>

              <div className="mt-4 flex flex-wrap gap-2 text-xs text-white/35">
                <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1.5">
                  Brand Setup
                </span>
                <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1.5">
                  Template
                </span>
                <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1.5">
                  Proposal
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  onSetDefault,
  onDelete,
  isDeleting,
  isSettingDefault,
  onClick,
}: {
  template: ProposalTemplate;
  onSetDefault: () => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  isSettingDefault: boolean;
  onClick: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const clientType = getTemplateClientType(template);
  const cfg = CLIENT_TYPE_CONFIG[clientType] ?? CLIENT_TYPE_CONFIG.other;

  const builderMode = getTemplateBuilderMode(template);
  const builderCfg = BUILDER_MODE_CONFIG[builderMode];

  const fixedPagePolicy = getTemplateFixedPagePolicy(template);
  const fixedCfg = FIXED_PAGE_POLICY_CONFIG[fixedPagePolicy];

  const serviceCount = getTemplateServiceCount(template);
  const pageCount = getTemplatePageCount(template);
  const taxRatePercent = getTemplateTaxRatePercent(template);
  const ready = isTemplateReady(template);

  return (
    <div
      onClick={onClick}
      className="group relative flex cursor-pointer items-center gap-4 rounded-xl border border-white/10 px-5 py-4 transition-all hover:border-white/20 hover:bg-white/[0.03]"
    >
      <div
        className={cn(
          "flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium",
          cfg.color
        )}
      >
        {cfg.icon}
        {cfg.label}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold">{template.name}</p>

          {template.is_default && (
            <span className="shrink-0 rounded-full border border-amber-500/20 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-400">
              Default
            </span>
          )}

          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em]",
              builderCfg.color
            )}
          >
            {builderCfg.icon}
            {builderCfg.label}
          </span>

          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em]",
              fixedCfg.color
            )}
          >
            {fixedCfg.icon}
            {fixedCfg.label}
          </span>

          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em]",
              ready
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300"
                : "border-white/10 bg-black/20 text-white/35"
            )}
          >
            <CheckCircle2 className="h-3 w-3" />
            {ready ? "Ready" : "Needs setup"}
          </span>
        </div>

        <p className="mt-0.5 text-xs leading-5 text-white/35">
          {serviceCount} service{serviceCount !== 1 ? "s" : ""} · {pageCount}{" "}
          page{pageCount !== 1 ? "s" : ""} · {taxRatePercent}% tax
          {template.description ? ` · ${template.description}` : ""}
        </p>

        <p className="mt-1 text-xs leading-5 text-white/28">
          {builderCfg.description} · {fixedCfg.description}
        </p>
      </div>

      <div
        className="flex items-center gap-1 opacity-0 transition-all group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {!confirmDelete ? (
          <>
            <Link
              to="/proposal-builder/new"
              search={{ templateId: template.id }}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-white/40 transition-colors hover:bg-white/10 hover:text-white/80"
              title="Use for proposal"
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>

            {!template.is_default ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSetDefault();
                }}
                disabled={isSettingDefault}
                className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-white/30 transition-colors hover:bg-amber-500/10 hover:text-amber-400 disabled:opacity-50"
                title="Set as default"
              >
                {isSettingDefault ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Star className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="px-2 py-1.5 text-amber-400/40">
                <StarOff className="h-3.5 w-3.5" />
              </span>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDelete(true);
              }}
              disabled={isDeleting}
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-red-400/60 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </button>
          </>
        ) : (
          <div className="flex items-center gap-1">
            <span className="text-xs text-white/40">Delete?</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(template.id);
              }}
              disabled={isDeleting}
              className="rounded-lg bg-red-500 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-50"
            >
              {isDeleting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                "Delete"
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDelete(false);
              }}
              className="rounded-lg bg-white/5 px-2.5 py-1.5 text-xs text-white/40 transition-colors hover:text-white"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <ChevronRight className="h-4 w-4 shrink-0 text-white/20 transition-colors group-hover:text-white/40" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-5 py-20 text-center">
      <div className="relative">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
          <FileText className="h-7 w-7 text-white/20" />
        </div>
        <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border border-amber-500/30 bg-amber-500/20">
          <Plus className="h-3.5 w-3.5 text-amber-400" />
        </div>
      </div>

      <div className="max-w-lg">
        <p className="text-lg font-semibold text-white/70">No templates yet</p>
        <p className="mt-2 text-sm leading-relaxed text-white/30">
          Templates are reusable proposal blueprints built on top of your Brand
          Setup. Create your first one to define the default services, pages,
          builder mode, fixed-page policy, and context used for a type of client or job.
        </p>
      </div>

      <Link
        to="/proposal-builder/templates/new"
        className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
      >
        <Plus className="h-4 w-4" />
        Create your first template
      </Link>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <div className="flex items-center gap-2 text-white/35">
        {icon}
        <span className="text-xs uppercase tracking-[0.14em]">{label}</span>
      </div>
      <p className="mt-3 text-lg font-semibold text-white/85">{value}</p>
    </div>
  );
}